// hooks/useBilling.ts — real Stripe-backed tier/status (1.23.00).
//
// Mirrors usePremium.ts's shape but reads from billingService (real data)
// rather than the static seed. Components must NOT import billingService
// directly.
//
// Fix-round F4(c): a failed or in-flight GET /billing/me/ is UNKNOWN, not
// "free". This hook used to swallow the rejection and hand back a fabricated
// { tier: 'free' } record, so a paying member whose fetch failed (or who
// simply hadn't finished loading) was shown a live "Upgrade to Pro" button —
// one click from a second subscription on the same account. `billing` now
// stays null until a real answer arrives, and `state` says which kind of
// nothing it is. Callers must render a neutral/loading state while
// `state !== 'ready'` — never the free-tier upgrade path.

import { useEffect, useState } from 'react';
import { billingService } from '@/core/services/billingService';
import type { BillingInfo } from '@/core/models/billing';

/**
 * 'idle'    — the caller disabled the fetch (e.g. a public page with no session).
 * 'loading' — request in flight; tier is UNKNOWN.
 * 'ready'   — `billing` is a real server answer.
 * 'error'   — the request failed; tier is UNKNOWN (never assume 'free').
 */
export type BillingState = 'idle' | 'loading' | 'ready' | 'error';

export interface BillingHook {
  /** The server's answer, or null while the tier is UNKNOWN (idle/loading/error). */
  billing: BillingInfo | null;
  /** True only after a SUCCESSFUL load — never true for a failed fetch. */
  loaded: boolean;
  /** True when the last fetch failed. `billing` stays null. */
  error: boolean;
  state: BillingState;
  /** Re-fetch (e.g. after returning from Checkout/Portal, or a Retry button). */
  refresh: () => void;
}

export interface UseBillingOptions {
  /** When false, no request is made at all and state stays 'idle' —
   * for public pages that render for signed-out visitors. */
  enabled?: boolean;
}

export function useBilling(options: UseBillingOptions = {}): BillingHook {
  const { enabled = true } = options;
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [state, setState] = useState<BillingState>(enabled ? 'loading' : 'idle');
  const [gen, setGen] = useState(0);

  useEffect(() => {
    if (!enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional loader flags, mirrors useMutedAccountsList.ts
      setBilling(null);
      setState('idle');
      return;
    }
    let cancelled = false;
    setState('loading');
    void billingService
      .me()
      .then((b) => {
        if (!cancelled) {
          setBilling(b);
          setState('ready');
        }
      })
      .catch(() => {
        if (!cancelled) {
          // UNKNOWN — deliberately NOT a fabricated free tier. See header.
          setBilling(null);
          setState('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gen, enabled]);

  return {
    billing,
    loaded: state === 'ready',
    error: state === 'error',
    state,
    refresh: () => setGen((g) => g + 1),
  };
}
